import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { ISignInConfigurationDTO } from '../../../../../../api/sign-in-configuration.dao';
import { SignInConfigurationDAO } from '../../../../../../api/implementations/sign-in-configuration.dao';
import { EnablementStatus, LinkTargetValue, SHORT_PLAIN_TEXT_LENGTH, SHORT_RICH_TEXT_LENGTH } from '../../../../../../constants/common.constants';
import {
    MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE,
    MAX_LENGTH_RICH_NOT_REQUIRED_ERROR_MESSAGE,
    TEXT_TOO_LONG_ERROR_MESSAGE
} from '../../../../../../constants/validation-errors';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { getFormattedUrl } from '../../../../../../infrastructure/utils/common.utils';
import { alertServerError, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import {
    convertBoolToDefaultOrCustomTypeForMixPanel,
    isTrackingFieldEnabled,
    TRACKING_EVENTS
} from '../../../../../../tracking-service/tracking-values';
import { HTML_TAGS_REG_EXP } from '../../../../../../constants/regex.constants';
import { checkForShourtPlainText } from '../../../../../../infrastructure/utils/common-validation';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../global/rich-text-editor/utils/transpiler';

const defaultValues: ISignInConfigurationDTO = {
    requestAccessCodeAutoSingInEnabled: false,
    requestAccessCodeRegistrationApprovalEnabled: false,
    isButtonAccessCodeRequestEnabled: true,
    isButtonAccessCodeSignInEnabled: true,
    header: 'Sign In',
    instructions: 'Please enter your email address below to request your Access Code',
    requestedMessage:
        'Thank you. If there is an Access Code associated with your email address, it will be sent to you by email. Please check your inbox.',
    requestButtonText: 'Request Access Code',
    navigationButtonText: 'Sign In with Access Code',
    loginErrorMessage: '',
    loginInstructions: '',
    signInPageLabel: 'Sign In',
    signInButtonLabel: 'Sign In',
    requestButtonLabel: 'Continue',
};
export default class SignInFormVM {
    @mobx.observable
    public isSaving: boolean;

    @mobx.observable
    public isLoading: boolean;

    @mobx.observable
    public requestAccessCodeAutoSingInEnabled: boolean;

    @mobx.observable
    public requestAccessCodeRegistrationApprovalEnabled: boolean;

    @mobx.observable
    public header: string = '';

    @mobx.observable
    public instructions: string = '';

    @mobx.observable
    public requestedMessage: string = '';

    @mobx.observable
    public isButtonAccessCodeRequestEnabled: boolean = true;
    @mobx.observable
    public requestButtonText: string = '';


    @mobx.observable
    public isButtonAccessCodeSignInEnabled: boolean = true;
    @mobx.observable
    public navigationButtonText: string = '';

    @mobx.observable
    public loginErrorMessage: string = '';

    @mobx.observable
    public loginInstructions: string = '';

    @mobx.observable
    public signInPageLabel: string = '';

    @mobx.observable
    private loginErrorMessageError: string;

    @mobx.observable
    private loginInstructionsError: string;

    @mobx.observable
    private signInPageLabelError: string;

    @mobx.observable
    private headerMessageError: string;

    @mobx.observable
    private instructionsMessageError: string;

    @mobx.observable
    private requestedMessageMessageError: string;

    @mobx.observable
    private requestButtonTextError: string;

    @mobx.observable
    private navigationButtonTextError: string;

    @mobx.observable
    public signInButtonLabel: string = '';
    @mobx.observable
    private signInButtonLabelError: string | JSX.Element;

    @mobx.observable
    public requestAccessCodeButtonLabel: string = '';
    @mobx.observable
    private requestAccessCodeButtonLabelError: string | JSX.Element;

    private signInConfigurationInitValues: ISignInConfigurationDTO;

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly subdomain: string,
        public readonly isAccountRequestAccessCodeRegistrationApprovalEnabled: boolean = false,
        @IoC.Inject private readonly signInConfigurationDAO: SignInConfigurationDAO = IoC.Container.get(SignInConfigurationDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) {
        this.isSaving = false;
        this.isLoading = false;
        this.requestAccessCodeRegistrationApprovalEnabled = false;
    }

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @mobx.action
    public async initialize() {
        await this.getSignInConfiguration();
    }

    @mobx.action
    public toggleRequestAccessCodeAutoSingIn = (): void => {
        this.requestAccessCodeAutoSingInEnabled = !this.requestAccessCodeAutoSingInEnabled;
    };

    @mobx.action
    public toggleRequestAccessCodeRegistrationApproval = (): void => {
        this.requestAccessCodeRegistrationApprovalEnabled = !this.requestAccessCodeRegistrationApprovalEnabled;
    };

    @mobx.action
    public setSignInPageLabel = (message: string): void => {
        this.checkSignInPageLabel(message);
        this.signInPageLabel = message;
    };

    public getSignInPageLabel(): string {
        return this.signInPageLabel;
    }

    public getSignInPageLabelError(): string {
        return this.signInPageLabelError;
    }

    @mobx.action
    public setSignInButtonLabel = (message: string): void => {
        this.signInButtonLabelError = checkForShourtPlainText('Sign In button label', message, true);
        this.signInButtonLabel = message;
    };

    public getSignInButtonLabel(): string {
        return this.signInButtonLabel;
    }

    public getSignInButtonLabelError(): string | JSX.Element {
        return this.signInButtonLabelError;
    }

    @mobx.action
    public setRequestAccessCodeButtonLabel = (message: string): void => {
        this.requestAccessCodeButtonLabelError = checkForShourtPlainText('Request Access Code button label', message, true);
        this.requestAccessCodeButtonLabel = message;
    };

    public getRequestAccessCodeButtonLabel(): string {
        return this.requestAccessCodeButtonLabel;
    }

    public getRequestAccessCodeButtonLabelError(): string | JSX.Element {
        return this.requestAccessCodeButtonLabelError;
    }

    @mobx.action
    public checkSignInPageLabel = (message: string): string => {
        if (message.length > SHORT_PLAIN_TEXT_LENGTH) {
            this.signInPageLabelError = MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        } else {
            this.signInPageLabelError = '';
        }

        return this.headerMessageError;
    };

    @mobx.action
    public setHeaderMessage = (message: string): void => {
        this.checkHeaderMessage(message);
        this.header = message;
    };

    public getHeaderMessageError(): string {
        return this.headerMessageError;
    }

    @mobx.action
    public checkHeaderMessage = (message: string): string => {
        if (message.length > SHORT_PLAIN_TEXT_LENGTH) {
            this.headerMessageError = MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        } else {
            this.headerMessageError = '';
        }

        return this.headerMessageError;
    };

    @mobx.action
    public setInstructionsMessage = (message: string): void => {
        this.checkInstructionsMessage(message);
        this.instructions = message;
    };

    @mobx.action
    public setLoginInstructions = (message: string): void => {
        this.checkLoginInstructions(message);
        this.loginInstructions = message;
    };

    public getLoginInstructions(): string {
        return this.loginInstructions;
    }

    public getLoginInstructionsError(): string {
        return this.loginInstructionsError;
    }

    @mobx.action
    public checkLoginInstructions = (message: string): string => {
        if (message.length > SHORT_RICH_TEXT_LENGTH) {
            this.loginInstructionsError = TEXT_TOO_LONG_ERROR_MESSAGE;
        } else {
            this.loginInstructionsError = '';
        }

        return this.loginInstructionsError;
    };

    @mobx.action
    public setLoginErrorMessage = (message: string): void => {
        this.checkLoginErrorMessage(message);
        this.loginErrorMessage = message;
    };

    public getLoginErrorMessage(): string {
        return this.loginErrorMessage;
    }

    public getLoginErrorMessageError(): string {
        return this.loginErrorMessageError;
    }

    @mobx.action
    public checkLoginErrorMessage = (message: string): string => {
        if (message.length > SHORT_RICH_TEXT_LENGTH) {
            this.loginErrorMessageError = TEXT_TOO_LONG_ERROR_MESSAGE;
        } else {
            this.loginErrorMessageError = '';
        }

        return this.loginErrorMessageError;
    };

    public getInstructionsMessageError(): string {
        return this.instructionsMessageError;
    }

    @mobx.action
    public checkInstructionsMessage = (message: string): string => {
        if (message.length > SHORT_RICH_TEXT_LENGTH) {
            this.instructionsMessageError = MAX_LENGTH_RICH_NOT_REQUIRED_ERROR_MESSAGE;
        } else {
            this.instructionsMessageError = '';
        }

        return this.instructionsMessageError;
    };

    @mobx.action
    public setRequestedMessage = (message: string): void => {
        this.checkRequestedMessage(message);
        this.requestedMessage = message;
    };

    public getRequestedMessageError(): string {
        return this.requestedMessageMessageError;
    }

    @mobx.action
    public checkRequestedMessage = (message: string): string => {
        if (message.length > SHORT_RICH_TEXT_LENGTH) {
            this.requestedMessageMessageError = MAX_LENGTH_RICH_NOT_REQUIRED_ERROR_MESSAGE;
        } else {
            this.requestedMessageMessageError = '';
        }

        return this.requestedMessageMessageError;
    };


    @mobx.action
    public setRequestButtonText = (message: string, intl: IntlShape): void => {
        this.checkRequestButtonText(message, intl);
        this.requestButtonText = message;
    };

    public getRequestButtonTextError(): string {
        return this.requestButtonTextError;
    }

    @mobx.action
    public checkRequestButtonText = (message: string, intl: IntlShape): string => {
        if (!message.length) {
            this.requestButtonTextError = intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Access Code Request button is required',
                id: 'oe.passport.requestButtonRequired',
            });
        } else if (message.length > SHORT_PLAIN_TEXT_LENGTH) {
            this.requestButtonTextError = MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        } else {
            this.requestButtonTextError = '';
        }

        return this.requestButtonTextError;
    };

    @mobx.action
    public toggleNavigationButtonTextEnabled = (): void => {
        this.isButtonAccessCodeSignInEnabled = !this.isButtonAccessCodeSignInEnabled;
    };

    @mobx.action
    public toggleRequestButtonTextEnabled = (): void => {
        this.isButtonAccessCodeRequestEnabled = !this.isButtonAccessCodeRequestEnabled;
    };

    @mobx.action
    public setNavigationButtonText = (message: string, intl: IntlShape): void => {
        this.checkNavigationButtonText(message, intl);
        this.navigationButtonText = message;
    };

    public getNavigationButtonTextError(): string {
        return this.navigationButtonTextError;
    }

    @mobx.action
    public checkNavigationButtonText = (message: string, intl: IntlShape): string => {
        if (!message.length) {
            this.navigationButtonTextError = intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Button navigation is required',
                id: 'oe.passport.buttonNavigationRequired',
            });
        } else if (message.length > SHORT_PLAIN_TEXT_LENGTH) {
            this.navigationButtonTextError = MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        } else {
            this.navigationButtonTextError = '';
        }

        return this.navigationButtonTextError;
    };

    public getValueForNotMandatoryField = (value: string | null, defaultValue: string): string | null => {
        let fieldValue = null;
        if (value === null) {
            fieldValue = defaultValue;
        } else if (value === '') {
            fieldValue = '';
        } else {
            fieldValue = value;
        }
        return fieldValue;
    };

    @mobx.action
    public getSignInConfiguration = async (): Promise<void> => {
        try {
            this.isLoading = true;
            const result = await this.signInConfigurationDAO.getSignInConfiguration(this.eventId, this.accountId);
            this.signInConfigurationInitValues = result;
            const {
                requestAccessCodeAutoSingInEnabled,
                requestAccessCodeRegistrationApprovalEnabled,
                header,
                instructions,
                requestedMessage,
                requestButtonText,
                navigationButtonText,
                loginErrorMessage,
                loginInstructions,
                signInPageLabel,
                signInButtonLabel,
                requestButtonLabel,
                isButtonAccessCodeRequestEnabled,
                isButtonAccessCodeSignInEnabled,
            } = result;

            mobx.runInAction(() => {
                this.requestAccessCodeAutoSingInEnabled = requestAccessCodeAutoSingInEnabled;
                this.requestAccessCodeRegistrationApprovalEnabled =
                    Boolean(requestAccessCodeRegistrationApprovalEnabled);
                this.header = this.getValueForNotMandatoryField(header, defaultValues.header);
                this.instructions = transpileInlineStylesToClasses(this.getValueForNotMandatoryField(instructions, defaultValues.instructions));
                this.requestedMessage = transpileInlineStylesToClasses(this.getValueForNotMandatoryField(requestedMessage, defaultValues.requestedMessage));
                this.requestButtonText = requestButtonText || defaultValues.requestButtonText;
                this.navigationButtonText = navigationButtonText || defaultValues.navigationButtonText;
                this.signInPageLabel = this.getValueForNotMandatoryField(signInPageLabel, defaultValues.signInPageLabel);
                this.loginErrorMessage = transpileInlineStylesToClasses(loginErrorMessage || defaultValues.loginErrorMessage);
                this.loginInstructions = transpileInlineStylesToClasses(loginInstructions || defaultValues.loginInstructions);
                this.signInButtonLabel = signInButtonLabel || defaultValues.signInButtonLabel;
                this.requestAccessCodeButtonLabel = requestButtonLabel || defaultValues.requestButtonLabel;
                this.isButtonAccessCodeRequestEnabled = isButtonAccessCodeRequestEnabled;
                this.isButtonAccessCodeSignInEnabled = isButtonAccessCodeSignInEnabled;
            });
        } catch (e) {
            SignInFormVM.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;

            });
        }
    };

    public checkValidity = (intl: IntlShape): boolean => {
        if (!this.checkHeaderMessage(this.header)
            && !this.checkInstructionsMessage(this.instructions)
            && !this.checkRequestedMessage(this.requestedMessage)
            && !(this.isButtonAccessCodeRequestEnabled && this.checkRequestButtonText(this.requestButtonText, intl))
            && !(this.isButtonAccessCodeSignInEnabled && this.checkNavigationButtonText(this.navigationButtonText, intl))
            && !this.checkLoginErrorMessage(this.loginErrorMessage)
            && !this.checkLoginInstructions(this.loginInstructions)
            && !this.checkHeaderMessage(this.signInPageLabel)
            && !this.requestAccessCodeButtonLabelError
            && !this.signInButtonLabelError
        ) return true;
        else return false;
    };

    @mobx.action
    public applySignInForm = async (intl: IntlShape): Promise<boolean> => {
        try {
            if (!this.checkValidity(intl)) return false;
            this.isSaving = true;

            const signInConfigurationData: ISignInConfigurationDTO = {
                requestAccessCodeAutoSingInEnabled: this.requestAccessCodeAutoSingInEnabled,
                requestAccessCodeRegistrationApprovalEnabled:
                    this.requestAccessCodeRegistrationApprovalEnabled,
                isButtonAccessCodeSignInEnabled: this.isButtonAccessCodeSignInEnabled,
                isButtonAccessCodeRequestEnabled: this.isButtonAccessCodeRequestEnabled,
                header: this.header,
                instructions: transpileClassesToInlineStyles(this.instructions),
                requestedMessage: transpileClassesToInlineStyles(this.requestedMessage),
                requestButtonText: !this.isButtonAccessCodeRequestEnabled
                    ? defaultValues.requestButtonText
                    : this.requestButtonText,
                navigationButtonText: !this.isButtonAccessCodeSignInEnabled
                    ? defaultValues.navigationButtonText
                    : this.navigationButtonText,
                loginErrorMessage: transpileClassesToInlineStyles(this.loginErrorMessage),
                loginInstructions: transpileClassesToInlineStyles(this.loginInstructions),
                signInPageLabel: this.signInPageLabel,
                signInButtonLabel: this.signInButtonLabel,
                requestButtonLabel: this.requestAccessCodeButtonLabel,
            };
            await this.signInConfigurationDAO.updateSignInConfiguration(this.accountId, this.eventId, {...signInConfigurationData});
            this.trackSignInConfigurationMixPanel(signInConfigurationData);

            showNotification('Form updated');
            return true;
        } catch (e) {
            SignInFormVM.handleApiException(e);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    public trackSignInConfigurationMixPanel = (signInConfigurationData: ISignInConfigurationDTO): void => {

        this.trackingService.get().track(TRACKING_EVENTS.SIGN_IN_CONFIGURATION, {
            'Sign In Page Label': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.signInPageLabel === signInConfigurationData.signInPageLabel
            ),
            'Login Instructions': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.loginInstructions === signInConfigurationData.loginInstructions
            ),
            'Login Error Message': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.loginErrorMessage === signInConfigurationData.loginErrorMessage
            ),
            'One-Step Request Access Code and Sign In': isTrackingFieldEnabled(
                signInConfigurationData.requestAccessCodeAutoSingInEnabled
            ),
            'Access Code Header': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.header === signInConfigurationData.header
            ),
            'Access Code Instructions': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.instructions === signInConfigurationData.instructions
                    .replace(HTML_TAGS_REG_EXP, '')
            ),
            'Access Code Requested Message': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.requestedMessage === signInConfigurationData
                    .requestedMessage.replace(HTML_TAGS_REG_EXP, '')
            ),
            'Access Code Request button': !this.isButtonAccessCodeRequestEnabled
                ? EnablementStatus.DISABLED_UPPERCASE
                : convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.requestButtonText === signInConfigurationData.requestButtonText
            ),
            'Button navigation on Access Code Sign In page': !this.isButtonAccessCodeSignInEnabled
                ? EnablementStatus.DISABLED_UPPERCASE
                : convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.navigationButtonText === signInConfigurationData.navigationButtonText
            ),
            'Request Access Code button label': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.requestButtonLabel === signInConfigurationData.requestButtonLabel
            ),
            'Sign In button label': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultValues.signInButtonLabel === signInConfigurationData.signInButtonLabel
            ),
        });
    };

    @mobx.action
    public openSignInFormPreview = (): void => {
        const link = getFormattedUrl(this.subdomain);
        window.open(link, LinkTargetValue.BLANK);
    };
}
